import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api } from "./client";
import type { UserPreferences } from "./types";

export const DEFAULT_PREFERENCES: UserPreferences = {
  command_palette_recents: [],
  layout: "kanban",
  timezone_override: null,
};

export type PreferencesValue = {
  readonly preferences: UserPreferences;
  readonly setLayout: (layout: "kanban" | "list") => void;
  // Optimistic write-through for the remaining preferences; persists to
  // /me/preferences.
  readonly update: (partial: Partial<UserPreferences>) => void;
};

const PreferencesContext = createContext<PreferencesValue | null>(null);

export const PreferencesProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const [preferences, setPreferences] =
    useState<UserPreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    let active = true;
    void api
      .get<UserPreferences>("/me/preferences")
      .then((loaded) => {
        if (!active) {
          return;
        }
        setPreferences(loaded);
      })
      .catch(() => {
        // No preferences yet / server unreachable: keep the defaults.
      });
    return () => {
      active = false;
    };
  }, []);

  // Optimistically update local state and fire-and-forget the persist. A
  // failed PATCH leaves the UI on the chosen value (best-effort persistence).
  const persist = useCallback((partial: Partial<UserPreferences>): void => {
    setPreferences((current) => ({ ...current, ...partial }));
    void api.patch("/me/preferences", partial).catch(() => {
      // Best-effort: keep the local choice even if the write fails.
    });
  }, []);

  const setLayout = useCallback(
    (layout: "kanban" | "list"): void => {
      persist({ layout });
    },
    [persist],
  );

  const value = useMemo<PreferencesValue>(
    () => ({ preferences, setLayout, update: persist }),
    [preferences, setLayout, persist],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = (): PreferencesValue => {
  const value = useContext(PreferencesContext);
  if (!value) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return value;
};
